var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlanTypeCategory, StatusPermission } from '../../../enum';
import { useSummaryReportAPI } from '../../annual/summaryReportApi';
import { useUserPermissionsAPI } from '../../app/selectors';
import { ErrorMessageBox, FallbackComponent } from '../../common';
import { AttritionContent } from '../attrition';
import { StepPanel } from '../common/stepContainer';
import { GENERAL_MONTH, LocationTableEntryMode } from '../constants';
import { WizardContext } from '../context';
import { OvertimeCostContent } from '../financial/overtimeCost';
import { CurrencySelect } from '../financial/staffSalaries';
import { HandleTimeContent } from '../handleTime';
import { LearningCurveContent } from '../learningCurve';
import { OccupancyTable, ProdHoursContent } from '../prodHours';
import { useQueueAPI, useQueuesAPI, useSaveQueueAPI } from '../queue/selectors';
import { useEffectiveCapacityPermission, useIsPlanLocked, usePlanType, usePlanTypeCategory, useQueueLocations, useWizardParams } from '../selectors';
import { ShrinkageContent, ShrinkageLocationSelector } from '../shrinkage';
import { ShrinkageUnitMode } from '../shrinkage/useShrinkageUnit';
import { SupportStaffTable } from '../supportStaff/locationRoleTable';
import { WorkArrivalTimeContent } from '../workArrivalPattern/timeContainer';
import { DailyWorkVolumeContent } from '../workArrivalPattern/volumeContainer';
import { WorkVolumeContent } from '../workVolume';
import { WorkingDayContent } from '../workingDays';
import { ActionBar } from './actionBar';
import { BulkChangeAccordion } from './bulkChange';
import { useCalculateAPI } from './selectors';
import { TitleBar } from './titleBar';
var BulkChangeShrinkage = function (_a) {
  var _b, _c, _d;
  var title = _a.title,
    setValue = _a.setValue,
    getValues = _a.getValues;
  var _e = useState(false),
    showCheckbox = _e[0],
    setShowCheckbox = _e[1];
  var _f = useState([]),
    selectedRows = _f[0],
    setSelectedRows = _f[1];
  var queueData = useQueueAPI()[0];
  var queueLocations = useMemo(function () {
    var _a, _b;
    return (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) !== null && _b !== void 0 ? _b : [];
  }, [(_b = queueData.data) === null || _b === void 0 ? void 0 : _b.queueLocations]);
  var hasQueueLocations = queueLocations.length > 0;
  var _g = useState(ShrinkageUnitMode.Percent),
    unitMode = _g[0],
    setUnitMode = _g[1];
  var _h = useState((_d = (_c = queueLocations[0]) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null),
    selectedQueueLocationId = _h[0],
    setSelectedQueueLocationId = _h[1];
  useEffect(function () {
    var _a, _b;
    setSelectedQueueLocationId((_b = (_a = queueLocations[0]) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null);
  }, [queueLocations]);
  return _jsxs(Stack, __assign({
    spacing: 0
  }, {
    children: [_jsx(BulkChangeAccordion, {
      title: title,
      name: ['shrinkages', 'percent'],
      setValue: setValue,
      getValues: getValues,
      setShowCheckbox: setShowCheckbox,
      selectedRows: selectedRows,
      selectedQueueLocationId: selectedQueueLocationId,
      decimal: 2,
      min: 0,
      max: 99.99,
      unitMode: unitMode
    }), hasQueueLocations && _jsx(Box, __assign({
      sx: {
        width: 240,
        mt: '0!important'
      }
    }, {
      children: _jsx(ShrinkageLocationSelector, {
        selectedQueueLocationId: selectedQueueLocationId,
        setSelectedQueueLocationId: function (id) {
          setSelectedQueueLocationId(id);
          setSelectedRows([]);
        }
      })
    })), _jsx(Box, __assign({
      sx: {
        mt: hasQueueLocations ? 4 : 0
      }
    }, {
      children: _jsx(ShrinkageContent, {
        showCheckbox: showCheckbox,
        setSelectedRows: setSelectedRows,
        selectedRows: selectedRows,
        queueLocationId: selectedQueueLocationId,
        isReview: true,
        unitMode: unitMode,
        setUnitMode: setUnitMode
      })
    }))]
  }));
};
export var Review = function () {
  var _a;
  var t = useTranslation('wizard').t;
  var permission = useUserPermissionsAPI();
  var planType = usePlanType();
  var planTypeCategory = usePlanTypeCategory();
  var queueData = useQueueAPI()[0];
  var queuesData = useQueuesAPI()[0];
  var _b = useCalculateAPI(),
    calculatedData = _b[0],
    fetchCalculate = _b[1],
    clearCalculatedData = _b[2];
  var navigate = useNavigate();
  var basePlanId = useWizardParams().basePlanId;
  var _c = useSummaryReportAPI(basePlanId),
    clearSummaryReport = _c[2];
  var savedData = useSaveQueueAPI()[0];
  var _d = useContext(WizardContext),
    handleSave = _d.handleSave,
    requesting = _d.requesting;
  var capacityPermission = useEffectiveCapacityPermission();
  var isPlanLocked = useIsPlanLocked();
  var isViewOnly = capacityPermission !== StatusPermission.Edit || isPlanLocked;
  var _e = useFormContext(),
    watch = _e.watch,
    getValues = _e.getValues,
    setValue = _e.setValue,
    control = _e.control;
  var _f = useState(GENERAL_MONTH),
    month = _f[0],
    setMonth = _f[1];
  var locations = useQueueLocations();
  var attritionEntryMode = watch('attritionEntryMode');
  var isPercentageAttrition = attritionEntryMode === LocationTableEntryMode.Percentage;
  var attritionConfig = isPercentageAttrition ? {
    decimal: 2,
    min: 0,
    max: 99.99
  } : {
    decimal: 2,
    min: 0,
    max: 9999
  };
  var disableFinancial = (permission === null || permission === void 0 ? void 0 : permission.financialPermission) === StatusPermission.Hide;
  useEffect(function () {
    if (calculatedData.data) {
      // The recalculation just produced fresh server-side numbers. The annual/report
      // summary is cached (useCachedAPI) and won't otherwise refetch on mount, so
      // invalidate it here — before navigating — so the report loads the new results
      // instead of the stale pre-edit cache.
      clearSummaryReport();
      navigate("/report/".concat(basePlanId));
    }
    return clearCalculatedData;
  }, [basePlanId, calculatedData.data, clearCalculatedData, clearSummaryReport, navigate]);
  if (!watch('id')) {
    return _jsx(FallbackComponent, {
      requesting: true
    });
  }
  return _jsxs(StepPanel, {
    children: [_jsx(ErrorMessageBox, {
      errors: [queueData.error, savedData.error, calculatedData.error]
    }), _jsx(FallbackComponent, __assign({
      backdrop: true,
      overlay: true,
      requesting: !!queueData.requesting || !!queuesData.requesting || requesting
    }, {
      children: _jsxs(Stack, __assign({
        component: "form",
        onSubmit: handleSave(isViewOnly ? 'submit' : 'calculate', function () {
          return isViewOnly ? navigate("/report/".concat(basePlanId)) : fetchCalculate();
        }),
        spacing: 4,
        "data-testid": "review-form"
      }, {
        children: [_jsx(TitleBar, {}), _jsx(ActionBar, {
          showQueue: true
        }), planTypeCategory === PlanTypeCategory.ProdHours ? _jsxs(Box, __assign({
          sx: {
            mt: 0
          }
        }, {
          children: [_jsx(BulkChangeAccordion, {
            name: ['volumes', 'productionHour'],
            title: t('prodHours.fullName'),
            setValue: setValue,
            getValues: getValues,
            decimal: 2,
            min: 0
          }), _jsx(ProdHoursContent, {}), _jsx(BulkChangeAccordion, {
            name: ['volumes', 'prodOccupancy'],
            title: t('prodHours.occupancy'),
            setValue: setValue,
            getValues: getValues,
            decimal: 2,
            min: 0.01,
            max: 100
          }), _jsx(OccupancyTable, {})]
        })) : _jsxs(Box, __assign({
          sx: {
            mt: 0
          }
        }, {
          children: [_jsx(BulkChangeAccordion, {
            name: ['volumes', 'volume'],
            title: t('workVolume.name'),
            setValue: setValue,
            getValues: getValues
          }), _jsx(WorkVolumeContent, {})]
        })), !!((_a = queueData.data) === null || _a === void 0 ? void 0 : _a.wapTimes.length) && _jsxs(Stack, __assign({
          direction: "column",
          gap: 2
        }, {
          children: [_jsx(Typography, __assign({
            variant: "subtitle1"
          }, {
            children: planTypeCategory === PlanTypeCategory.ProdHours ? t('wapTime.titleRequired') : t('wapTime.title')
          })), _jsx(Typography, {
            children: t('wapTime.reviewDescription')
          }), _jsx(WorkArrivalTimeContent, {}), _jsx(DailyWorkVolumeContent, {
            setMonth: setMonth,
            month: month
          })]
        })), planTypeCategory === PlanTypeCategory.WorkVolume && _jsxs(_Fragment, {
          children: [_jsx(BulkChangeAccordion, {
            name: ['volumes', 'handleTime'],
            title: t('handleTime.shortName'),
            setValue: setValue,
            getValues: getValues,
            max: 99999
          }), _jsx(HandleTimeContent, {}), _jsx(BulkChangeAccordion, {
            title: t('learnCurve.reviewTitle'),
            name: ['volumes', 'learnCurve'],
            setValue: setValue,
            getValues: getValues,
            max: 100,
            usePeriodLabels: true
          }), _jsx(LearningCurveContent, {})]
        }), _jsx(BulkChangeShrinkage, {
          title: t('shrinkage.shortName'),
          setValue: setValue,
          getValues: getValues,
          control: control
        }), _jsx(BulkChangeAccordion, __assign({
          name: ['volumes', 'attrition'],
          title: t('attrition.fullName'),
          setValue: setValue,
          getValues: getValues
        }, attritionConfig)), _jsx(AttritionContent, {}), _jsxs(Stack, __assign({
          spacing: 0
        }, {
          children: [_jsx(Typography, __assign({
            variant: "subtitle1",
            sx: {
              mb: 2
            }
          }, {
            children: t('workingDay.title', {
              planType: planType
            })
          })), _jsx(WorkingDayContent, {})]
        })), _jsxs(Stack, __assign({
          spacing: 2
        }, {
          children: [_jsxs(Stack, __assign({
            direction: "row",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 2
          }, {
            children: [_jsx(Typography, __assign({
              variant: "subtitle1"
            }, {
              children: t('supportStaff.supportStaffAndFinancials')
            })), (permission === null || permission === void 0 ? void 0 : permission.financialPermission) !== StatusPermission.Hide && !locations.length && _jsx(CurrencySelect, {})]
          })), _jsx(Box, {
            children: _jsx(SupportStaffTable, {
              disableFinancial: disableFinancial,
              isReview: true
            })
          })]
        })), _jsx(Typography, __assign({
          variant: "subtitle1",
          sx: {
            pb: 2
          }
        }, {
          children: t('review.overtimeCosts')
        })), _jsx(OvertimeCostContent, {
          disableDescription: true
        }), _jsx(ActionBar, {})]
      }))
    }))]
  });
};